import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Eye, Wind, Sprout, ArrowRight } from "lucide-react";
import { PageHeader, SectionHeading } from "../components/PageBits";
import { Reveal } from "../components/Reveal";
import { JOURNEY } from "../lib/journey";

const PILLARS = [
  {
    icon: Eye,
    t: "Conciencia y comprensión",
    d: [
      "Comprende cómo se forman tus pensamientos, creencias, emociones y patrones de comportamiento.",
      "Aprende a observar tu historia con mayor claridad para reconocer aquello que hoy influye en tu manera de vivir.",
    ],
  },
  {
    icon: Wind,
    t: "Regulación emocional y sistema nervioso",
    d: [
      "Aprende a reconocer lo que sientes, comprender las respuestas de tu cuerpo y desarrollar herramientas para recuperar seguridad interna.",
      "La regulación permite que el cambio no ocurra solo en la mente, sino también en la experiencia cotidiana.",
    ],
  },
  {
    icon: Sprout,
    t: "Transformación e integración",
    d: [
      "Convierte la comprensión en decisiones, hábitos y nuevas formas de relacionarte contigo, con los demás y con tu vida.",
      "Integrar significa llevar lo aprendido más allá de la teoría y convertirlo en una manera diferente de vivir.",
    ],
  },
];

const GAPS = [
  "Puedes aprender técnicas para calmar la ansiedad sin entender por qué aparece.",
  "Puedes leer sobre el perdón sin haber mirado la culpa que lo bloquea.",
  "Puedes regular tu sistema nervioso sin haber comprendido qué historia está intentando expresar tu cuerpo.",
];

export default function Academy() {
  return (
    <>
      {/* 1. Hero */}
      <PageHeader
        overline="El método"
        titleLines={["No puedes sanar", "lo que no comprendes."]}
        accentLines={["Ni transformar", "lo que no has sanado."]}
        subtitle="La mayoría de los enfoques de desarrollo personal tratan la mente, el cuerpo y el espíritu como si fueran asuntos separados. Aquí partimos de una idea distinta: comprender, sanar y transformar son fases de un mismo proceso, y el orden en que se recorren importa tanto como el contenido de cada una."
      />

      {/* 2. El problema que nadie nombra bien */}
      <section className="relative bg-cream py-24 text-navy sm:py-32" data-testid="academy-problem">
        <div className="container-luxe">
          <SectionHeading
            dark={false}
            overline="Lo que falta en la mayoría de los enfoques"
            title="Tratar el síntoma no es lo mismo que"
            accent="comprender su origen."
          />
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-12">
            <div className="flex flex-col divide-y divide-navy/10 lg:col-span-7">
              {GAPS.map((g, i) => (
                <Reveal key={g} delay={i * 0.1} className="py-6 first:pt-0">
                  <p className="flex gap-4 text-lg font-light leading-relaxed text-navy/80">
                    <span className="font-serif text-2xl leading-none text-gold-deep">—</span>
                    {g}
                  </p>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2} className="lg:col-span-5 lg:pt-2">
              <p className="text-base font-light leading-relaxed text-navy/70 sm:text-lg">
                Muchos enfoques ofrecen herramientas valiosas, pero suelen concentrarse en una sola
                dimensión del ser humano. Nuestra metodología propone integrarlas para comprender el
                proceso completo.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 3. El método — tres pilares */}
      <section className="grain relative overflow-hidden bg-navy py-24 sm:py-32" data-testid="academy-method">
        <div className="container-luxe relative z-10">
          <SectionHeading
            overline="Nuestro enfoque"
            title="Tres dimensiones."
            accent="Un mismo proceso."
            subtitle="La transformación profunda requiere comprender lo que ocurre en tu mente, aprender a regular lo que sucede en tu cuerpo y convertir esa comprensión en una nueva forma de vivir. Estas tres dimensiones trabajan juntas dentro del recorrido de la Academia."
          />
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {PILLARS.map((p, i) => (
              <Reveal key={p.t} delay={i * 0.1}>
                <motion.div
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                  className="flex h-full flex-col gap-5 rounded-sm border border-gold/20 bg-navy-soft/30 p-8 backdrop-blur-sm transition-colors duration-300 hover:border-gold/50"
                  data-testid={`academy-pillar-${i}`}
                >
                  <div className="flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 text-gold">
                    <p.icon size={20} />
                  </div>
                  <h3 className="font-serif text-2xl font-normal leading-snug text-cream sm:text-3xl">{p.t}</h3>
                  <div className="flex flex-col gap-4">
                    {p.d.map((para, j) => (
                      <p key={para} className="text-sm font-light leading-relaxed text-cream/70">
                        {para}
                      </p>
                    ))}
                  </div>
                </motion.div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.3}>
            <p className="mt-12 max-w-2xl text-base font-light leading-relaxed text-cream/70 sm:text-lg">
              Cada dimensión aporta una perspectiva diferente. Juntas forman un proceso coherente que
              acompaña la transformación completa.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 4. La arquitectura del recorrido */}
      <section className="relative bg-cream py-24 text-navy sm:py-32" data-testid="academy-architecture">
        <div className="container-luxe">
          <SectionHeading
            dark={false}
            overline="Por qué el orden importa"
            title="No se puede regular lo que"
            accent="no se comprende."
            subtitle="El recorrido sigue una secuencia intencional:"
          />
          <div className="grid grid-cols-2 gap-x-8 gap-y-10 sm:grid-cols-3 lg:grid-cols-6">
            {JOURNEY.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.08}>
                <div className="flex flex-col gap-3" data-testid={`academy-stage-${s.n}`}>
                  <span className="font-serif text-4xl text-gold-deep">0{s.n}</span>
                  <span className="h-px w-8 bg-gold-deep/40" />
                  <h3 className="font-serif text-2xl leading-tight text-navy">{s.title}</h3>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <p className="mt-16 max-w-2xl text-base font-light leading-relaxed text-navy/70 sm:text-lg">
              Cada etapa prepara el camino para la siguiente. El objetivo no es avanzar más rápido,
              sino construir un cambio profundo y sostenible.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 5. Detrás del método — marcador temporal */}
      <section className="grain relative overflow-hidden bg-navy py-24 sm:py-32" data-testid="academy-founder-placeholder">
        <div className="container-luxe relative z-10">
          <SectionHeading overline="Detrás del método" title="José C. Guevara" />
          <Reveal>
            <p className="max-w-xl text-base font-light italic leading-relaxed text-cream/50">
              Sección en preparación. Próximamente se incorporará el texto definitivo, basado en la
              historia real del fundador.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 6. Cierre */}
      <section className="grain relative overflow-hidden bg-navy-deep py-24 sm:py-32" data-testid="academy-closing">
        <div className="container-luxe relative z-10 max-w-3xl">
          <Reveal>
            <p className="font-serif text-2xl font-light leading-snug text-cream sm:text-3xl">
              Este método toma forma concreta en once módulos que puedes recorrer a tu propio ritmo,
              cada uno ocupando un lugar dentro de esta misma arquitectura.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <Link
              to="/library"
              className="group mt-8 inline-flex items-center gap-2 text-base text-gold underline-offset-4 transition-colors hover:underline"
              data-testid="academy-closing-link"
            >
              Ver el recorrido completo
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>
    </>
  );
}
